import { z } from "zod";
import {
  timestampToDateNullable,
  serverStampDate,
} from "@/core/firebase/converters";
import {
  ClubFeaturesSchema,
  DEFAULT_CLUB_FEATURES,
} from "@/core/models/club-features";
import {
  PlatformFeaturesSchema,
  DEFAULT_PLATFORM_FEATURES,
} from "@/core/models/platform-features";
import {
  WalletPolicySchema,
  DEFAULT_WALLET_POLICY,
} from "@/core/models/wallet";
import { PricingRuleSchema } from "@/core/models/court";
import {
  ClubStatusSchema,
  BillingStatusSchema,
  type ClubAccessLevel,
  clubAccessLevel,
} from "@/core/models/club-status";
import { SubscriptionSchema } from "@/core/models/subscription";

/**
 * Activation event entry — when a milestone first occurred on a club +
 * who triggered it. Used inside `clubs/{id}.activation.{eventName}` map
 * for spec/16 BR-3 / BR-13 activation tracking.
 */
export const ActivationEventSchema = z.object({
  at: timestampToDateNullable.nullable().optional(),
  by: z.string().default(""),
});
export type ActivationEvent = z.infer<typeof ActivationEventSchema>;
export const ACTIVATION_EVENT_KEYS = [
  "firstCourtAt",
  "firstPricingRuleAt",
  "firstBookingAt",
  "firstSaleAt",
  "firstStaffInviteAt",
  "firstAiNarrativeAt",
] as const;
export type ActivationEventKey = (typeof ACTIVATION_EVENT_KEYS)[number];

/**
 * Default IANA timezone for a club's local clock. Every Phase-1 Partner Club
 * is in Spain, so this is the fallback when a club has no explicit
 * `timezone`. Exported as the single source of truth so storage-side code
 * (e.g. `dateKey` writes) can anchor to the CLUB day rather than the
 * writer's runtime zone — critical for UTC Cloud Functions and any
 * non-Spain admin. Phase 2 multi-country: pass the club's own `timezone`.
 */
export const DEFAULT_CLUB_TIMEZONE = "Europe/Madrid";

/**
 * Club document. Stored at `clubs/{clubId}` (top-level, not a subcollection).
 */

// ── Pricing-update policy ─────────────────────────────────────────────────
// Governs what happens to `paymentAmount` when a booking's court / time /
// duration changes (edit form OR drag-resize). The matching pricing rule
// is always available for inspection — this only controls whether we
// auto-apply it. Manual overrides (paymentAmount that doesn't match any
// rule) are NEVER auto-rewritten regardless of the policy: an operator
// who quoted €25 to a regular keeps €25.
//
//   auto  — silently apply the new rule price; show an undo toast
//   warn  — show "regla sugiere €X" inline + apply only on operator action
//   never — never recompute; don't even surface the suggestion
export const PRICING_UPDATE_POLICY = ["auto", "warn", "never"] as const;
// Single source of truth for the default. `auto` so that changing a booking's
// duration/court/time re-prices it without an extra operator step (rule-priced
// bookings adopt the new rule price; manual overrides scale proportionally).
export const DEFAULT_PRICING_UPDATE_POLICY = "auto" as const;
export const PricingUpdatePolicySchema = z
  .enum(PRICING_UPDATE_POLICY)
  .default(DEFAULT_PRICING_UPDATE_POLICY);
export type PricingUpdatePolicy = z.infer<typeof PricingUpdatePolicySchema>;

// Brand identity — nested map on the club document. The accent color is
// applied as the app's `--accent` token whenever this club is the active
// one, driving the brand chip ("PS" avatar in the sidebar), primary buttons,
// active nav highlight, focus rings, and every other accent surface. When
// missing/null, the app falls back to the default uPadelMatch cyan.
export const ClubBrandSchema = z.object({
  /** Hex color string (#rrggbb). Validated case-insensitively. */
  accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
});
export type ClubBrand = z.infer<typeof ClubBrandSchema>;

// Geographic location — nested map on the club document. Mirrors the
// `GeoLocation` class in usersmatch; keep fields in sync.
export const ClubLocationSchema = z.object({
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  /** Geohash precision 9 for proximity queries. */
  geohash: z.string().nullable().optional(),
  /** ISO 3166-1 alpha-2 (e.g. "ES"). */
  countryCode: z.string().nullable().optional(),
  /** Partition key: "{countryCode}_{city.toLowerCase()}" (e.g. "ES_granada"). */
  cityKey: z.string().nullable().optional(),
  /** Google Places ID — stable canonical reference. */
  placeId: z.string().nullable().optional(),
  formattedAddress: z.string().nullable().optional(),
  /**
   * How the pin was set: `manual` = an operator picked it in the in-app Maps
   * picker (authoritative — the `on_club_geocode` CF never overwrites it);
   * `geocoded` = the CF autofilled it from `address`. Absent on legacy docs.
   */
  source: z.enum(["manual", "geocoded"]).nullable().optional(),
});
export type ClubLocation = z.infer<typeof ClubLocationSchema>;

export function clubLocationHasCoordinates(l: ClubLocation): boolean {
  return l.latitude != null && l.longitude != null;
}

/** City partition key from countryCode + city. */
export function computeCityKey(
  countryCode: string | null | undefined,
  city: string | null | undefined,
): string | null {
  if (!countryCode || !city) return null;
  return `${countryCode.toLowerCase()}_${city.toLowerCase()}`;
}

/**
 * `defaultPricingRules` in Firestore is a `Map<ruleId, rule>` — same shape as
 * court.pricingRules. This preprocess flattens it into an array.
 */
const defaultPricingRulesMapToArray = z.preprocess((value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).map(
      ([id, rule]) => ({
        id,
        ...(rule as Record<string, unknown>),
      }),
    );
  }
  return [];
}, z.array(PricingRuleSchema));

export const ClubSchema = z.object({
  id: z.string(),
  name: z.string().default(""),
  /**
   * Audit-only: uid of whoever created the club. Immutable, grants NO
   * permissions. Source of truth for "is this user an owner" is the staff
   * subcollection (`clubs/{clubId}/staff/{uid}.role === 'owner'`); a club
   * may have any number of co-equal owners. Kept here so admin views and
   * support can answer "who originally set this up" without joining staff.
   */
  createdBy: z.string().default(""),
  address: z.string().default(""),
  city: z.string().default(""),
  /** Administrative-level-2 (Spain: provincia). Filled from Google Places
   *  `administrative_area_level_2`. */
  province: z.string().default(""),
  country: z.string().default(""),
  phone: z.string().default(""),
  /**
   * The number players message from the app. Feeds `places_courts.whatsappNumber`
   * (spec/usersmatch/01), which is what gates "Pedir pista" — usersmatch hides
   * that button, and the WhatsApp link, when there is no number.
   *
   * ⚠️ WITHOUT THIS, THE ONE THING A PLAYER CAN DO WITH A CLUB IS INVISIBLE.
   * Measured 2026-08-10: 48 clubs in the production directory, ZERO with a
   * number, so "Pedir pista" — shipped in usersmatch v0.1.28 — has never been
   * usable for a single club. The projection was even writing an explicit
   * `whatsappNumber: None`.
   *
   * Separate from `phone` on purpose: a landline at reception is not a number
   * anyone can WhatsApp, and silently reusing it would send players into a
   * conversation the club never receives.
   */
  whatsapp: z.string().default(""),
  /**
   * The usersmatch directory row this club is linked to — written BACK by the
   * projection CF, which is the only place that knows both ids. Read-only here.
   *
   * It exists so the club side can build the QR that sends players to its page
   * (`/unete/{playerAppPlaceId}`). Empty until an operator links the club: a
   * club that has asked but is not linked yet has no page to point at, and a
   * QR to nowhere on a club's door is worse than no QR.
   */
  playerAppPlaceId: z.string().default(""),
  email: z.string().default(""),
  /** NIF / CIF — Número de Identificación Fiscal. Required for factura. */
  nif: z.string().nullable().optional(),
  logoUrl: z.string().nullable().optional(),
  slug: z.string().default(""),
  features: ClubFeaturesSchema.default(DEFAULT_CLUB_FEATURES),
  /* Ventas' quick-pick catalogue lives in the `catalog_categories` +
   * `catalog_products` subcollections (spec/clubmanager/15), not a nested
   * `salesConfig` map on the club doc. */
  /**
   * Platform-controlled feature flags. NOT club-editable — written only
   * by uPadelMatch admins (via Firestore console / Admin SDK). Firestore
   * rules deny client writes to this field. See `platform-features.ts`.
   */
  platformFeatures: PlatformFeaturesSchema.default(DEFAULT_PLATFORM_FEATURES),
  /**
   * Wallet sub-config (bono default expiry, top-up bonus brackets). NOT the
   * on/off switch — activation is the owner opt-in `features.walletEnabled`
   * (self-serve GA). See `core/models/wallet.ts`, spec/clubmanager/18.
   */
  walletPolicy: WalletPolicySchema.default(DEFAULT_WALLET_POLICY),
  /** Per-club brand identity. Owner-editable. Drives `--accent`. */
  brand: ClubBrandSchema.default({}),
  /** e.g. `{ monday: { open: '08:00', close: '22:00' }, ... }`. Clock times
   *  are interpreted in `timezone` (below). */
  openingHours: z.record(z.string(), z.unknown()).default({}),
  /**
   * IANA timezone for this club's local clock — anchors `openingHours`,
   * the analytics heatmap hour bucketing, email send-time formatting, and
   * `bookingDateKeyFor`. Defaults to `'Europe/Madrid'` because every Phase-1
   * Partner Club is in Spain; the field exists so a non-Spain club can
   * override without code changes when Phase 2 opens (e.g. Portuguese clubs
   * → `'Europe/Lisbon'`, Mexican → `'America/Mexico_City'`).
   *
   * Why not derive from `countryCode`? IANA names are zone-specific (not
   * country-specific) — `countryCode='US'` could be any of 5 timezones.
   * Carrying the IANA name explicitly avoids the ambiguity and lets us add
   * cities in zones with seasonal DST rules without changing the model.
   */
  timezone: z.string().default(DEFAULT_CLUB_TIMEZONE),
  /** ISO-4217 operational currency for the club's own prices (court fees,
   *  catalogue, sales). Defaults EUR (Spain today). The seam for
   *  international clubs — formatting/aggregation read this instead of a
   *  hardcoded EUR. Distinct from uPadelMatch's own subscription billing,
   *  which stays EUR by business model. */
  currency: z.string().default("EUR"),
  /** BCP-47 locale for the club's number / date / currency formatting.
   *  Defaults es-ES. Field-only seam for i18n later. */
  locale: z.string().default("es-ES"),
  location: ClubLocationSchema.default({}),
  status: ClubStatusSchema.default("trial"),
  trialEndDate: timestampToDateNullable.nullable().optional(),
  emailVerified: z.boolean().default(false),
  defaultSlotDurationMinutes: z.number().int().default(90),
  /**
   * Grace window (minutes) after a booking's start before it surfaces in
   * the dashboard "Acciones pendientes" queue as *sin presentar* (the
   * Presentar / Ausente check-in action). Padel players routinely arrive a
   * few minutes late, so flagging at minute 0 spams reception with rows
   * that self-resolve. By minute `checkInGraceMinutes` a no-show is
   * genuinely actionable. Court-turnover economics differ a lot between a
   * 4-court neighbourhood club and a 12-court centre, so this is a real
   * operational lever — owner-tunable in Ajustes → Preferencias. Default 15.
   * Read by `bookingsOverdue()` in dashboard-kpis.ts.
   */
  checkInGraceMinutes: z.number().int().min(0).max(120).default(15),
  /**
   * How long a player's online-booking hold (`BookingHold`, spec/usersmatch/01)
   * stays `pending` before `expire_holds` reclaims the slot. Clamped 5–30 by
   * `holdExpiryFor` (booking-hold.ts) and `_hold_minutes_for` (main.py) —
   * both already read this field; this default is what every club got before
   * the setting existed, so leaving it untouched changes nothing. Owner-tunable
   * in Ajustes → Módulos → Reservas online → Configurar (gated on
   * `features.onlineBookings`).
   */
  holdDurationMinutes: z.number().int().min(5).max(30).default(15),
  /**
   * How many hours after a booking's end time "Restaurar" (undo an accidental
   * no-show, `booking-detail-panel.tsx`) stays available. Court-turnover
   * culture varies — some clubs want same-shift-only recovery, others run
   * looser. Default 4 matches the constant every club had before this was a
   * setting. Owner-tunable in the same panel as `holdDurationMinutes`.
   */
  noShowRecoveryHours: z.number().int().min(1).max(24).default(4),
  /**
   * How many days ahead a player may book online through usersmatch (today =
   * day 0). Ships with default 2 — today + tomorrow, the fixed Hoy/Mañana
   * window every club had before this existed — so activating this field
   * changes nothing for a club that never touches Ajustes. Enforced
   * server-side in `create_booking_hold` / `get_club_availability`
   * (main.py) — the window was previously UNENFORCED there (any far-future
   * date was silently accepted), so this field closes that gap as well as
   * adding the setting.
   */
  advanceBookingDays: z.number().int().min(1).max(30).default(2),
  /**
   * Max students on a **curso** (class series) roster (#101 v1.5). A padel
   * court physically holds 4, but real escuelas vary — adult groups run 4,
   * youth / mini-pádel run 5–6 (SUMA normativa: *"menores de 13 años, máximo
   * 5"*), some school programs rotate up to ~8 — and a `1` is a recurring
   * **private lesson** (clase particular fija). So the curso roster cap is an
   * owner-tunable policy, not a hardcoded number. Default 4. Range 1–8. Applies
   * ONLY to `class` series; `match` series stay hard-capped at 4 (the physical
   * on-court max). Owner-tunable in Ajustes → Módulos → Entrenadores →
   * Configurar (gated on coachRoleLive).
   * Validated 2026-07-11 (market research — see research doc §10).
   */
  maxClassRoster: z.number().int().min(1).max(8).default(4),
  defaultPricingRules: defaultPricingRulesMapToArray.default([]),
  pricingUpdatePolicy: PricingUpdatePolicySchema.default(
    DEFAULT_PRICING_UPDATE_POLICY,
  ),
  /**
   * Owner-controlled toggles for operational guardrails — spec/13 §Policies.
   * Read at the decision point (form submit, sale-grid render); never
   * snapshotted on the booking/sale doc, so flipping the toggle takes effect
   * immediately for new actions without rewriting historical data.
   */
  /** When false, the booking form's "Anónimo" tile is hidden — every new booking must link a CRM person/group. Defaults true (today's behaviour). */
  allowAnonymousBookings: z.boolean().default(true),
  /** When true, recording a payment that leaves the booking paid below `partialReasonThresholdPct` requires a non-empty `reason`. */
  requireReasonOnPartial: z.boolean().default(false),
  /** Threshold (% of total due) under which `requireReasonOnPartial` triggers. Ignored when the toggle is off. */
  partialReasonThresholdPct: z.number().int().min(0).max(100).default(80),
  /** When true, the "+ Otro" free-text tile on the sale POS grid is hidden for non-manager roles. */
  restrictFreeTextSalesToManager: z.boolean().default(false),
  /** Null for pre-claim mockup clubs — a subscription is only created on trial start. */
  subscription: SubscriptionSchema.nullable().optional(),
  /**
   * Cached count of `clubs/{id}/courts` docs where `status === 'active'`.
   * Single source of truth for billing quantity AND operational displays:
   * the same number drives Stripe `subscription_item.quantity`, the picker
   * row, the dashboard sidebar chip, and per-court analytics totals.
   *
   * Maintained by the `on_court_written` Firestore trigger — every court
   * create / update / delete recomputes the count and writes it here, then
   * mirrors the value to Stripe with proration. Never set by client code.
   *
   * Why cached on the club doc instead of computed live: list views
   * (admin club picker, multi-club admin dashboard) would otherwise
   * N+1-query the `courts` subcollection per row.
   */
  activeCourtCount: z.number().int().nonnegative().default(0),
  isDemo: z.boolean().default(false),
  demoCreatedAt: timestampToDateNullable.nullable().optional(),
  /**
   * In-flight owner-invite for a mockup club. Set when a preview viewer
   * requests the trial, cleared on conversion (when the owner accepts).
   * Only meaningful while `status === 'mockup'`. See
   * docs/preview-mockup-and-trial-flow.md.
   */
  previewTrialRequested: z
    .object({
      email: z.string().default(""),
      requestedAt: timestampToDateNullable.nullable().optional(),
      staffInviteId: z.string().default(""),
    })
    .nullable()
    .optional(),
  /**
   * Set when an owner requests club deletion. Disambiguates `closed` —
   * if non-null the ClubGate routes to /pending-deletion (holding page);
   * if null the club is treated as a payment-suspension and routed to
   * /suspended (reactivation prompt). Cleared by `delete_club_hard` only
   * after the recursive wipe completes.
   */
  deletionRequestedAt: timestampToDateNullable.nullable().optional(),
  /**
   * Admin escape hatch shared by every automated cleanup job:
   * `cleanup_mockup_clubs_daily` (abandoned mockups — token expiry,
   * owner-invite grace, orphan age) and `cleanup_lapsed_trial_clubs_daily`
   * (lapsed-unpaid trials past `subscription.graceEndDate`, Decision #17).
   * When true, the relevant job skips this club regardless of its own
   * timing rules. Meaningful while `status === 'mockup'` OR `status ===
   * 'trial'`; ignored otherwise. Set from the admin tokens drawer when
   * sales wants to keep a mockup prospect alive past the default window —
   * there is no in-app toggle yet for the trial-lapse case (rare enough
   * today for a manual Firestore write; add one if that changes).
   */
  noAutoCleanup: z.boolean().default(false),
  /**
   * Per-club overrides for the cleanup CF's grace windows. Missing keys
   * fall back to platform defaults declared in
   * `apps/clubmanager-pwa/functions/mockup_cleanup.py`:
   *   - ownerInviteGraceDays  (default 7)  — days after `previewTrialRequested`
   *     before the unaccepted invite is considered ignored.
   *   - orphanGraceDays       (default 30) — days from `createdAt` for a
   *     mockup with no preview tokens before it's auto-deleted.
   *
   * Use to extend (or shorten) the window for a specific prospect — e.g.
   * a high-value lead that needs 30 days to decide. The simpler on/off
   * `noAutoCleanup` flag still wins when both are set. Only meaningful
   * while `status === 'mockup'`.
   */
  cleanupConfig: z
    .object({
      // `.nullable()` because the admin UI writes `null` when the input is
      // cleared — same semantics as missing (= fall back to platform
      // default). Without nullable Zod rejects the null and the whole
      // club doc silently fails to parse.
      ownerInviteGraceDays: z.number().int().positive().nullable().optional(),
      orphanGraceDays: z.number().int().positive().nullable().optional(),
    })
    .nullable()
    .optional(),
  isVerified: z.boolean().default(false),
  verifiedAt: timestampToDateNullable.nullable().optional(),
  verifiedBy: z.string().nullable().optional(),
  /**
   * Billing health — orthogonal to the lifecycle `status`. `not_billable` =
   * mockup/trial (pre-billing); `current` = paying; `past_due` = read-only;
   * `exempt` = platform-admin escape hatch (the former `subscriptionExempt`
   * flag — never charged, always full access). Owned by the Stripe webhook
   * (`current`/`past_due`) + the admin exempt toggle (`exempt`); the Firestore
   * rule restricts owner writes. See `docs/data-model.md`.
   */
  billingStatus: BillingStatusSchema.default("not_billable"),
  /** Audit: timestamp of the most recent admin billingStatus override (exempt). */
  billingStatusSetAt: timestampToDateNullable.nullable().optional(),
  /** Audit: uid (or email) of the platform admin who set it. */
  billingStatusSetBy: z.string().nullable().optional(),
  /**
   * First time any court was saved or the pricing rules were edited after
   * the trial started. Used by `OWNER_TUTORIAL` to auto-complete the
   * "Revisa precios y pistas" step. Written by `courts-repository.update`
   * and the pricing-settings save path; merged with `serverTimestamp()`
   * so the first write wins.
   */
  pricesReviewedAt: timestampToDateNullable.nullable().optional(),
  /** Owner accepted publishing the reviewed prices while activating usersmatch. */
  playerAppPricesAcceptedAt: timestampToDateNullable.nullable().optional(),
  /**
   * First time the owner opened the Rendimiento (analytics) page. Used
   * by `OWNER_TUTORIAL` to auto-complete the "Revisa tu Rendimiento"
   * step (replaces the prior "Crea tu primera reserva" step which was
   * receptionist-shaped, not owner-shaped — owners care about KPIs
   * before they care about taking bookings). Written by
   * `markAnalyticsViewed()` on first analytics-page mount when the
   * caller is an owner; merged with `serverTimestamp()`, first write
   * wins.
   */
  analyticsViewedAt: timestampToDateNullable.nullable().optional(),
  /**
   * Owner confirmation of courts (historic values also include page visits). Used by `OWNER_TUTORIAL` to
   * auto-complete the "Revisa tus pistas" step. Written by
   * `confirmSetupStep()` on explicit owner acknowledgement;
   * `serverTimestamp()` with first-write-wins.
   */
  courtsReviewedAt: timestampToDateNullable.nullable().optional(),
  /**
   * Owner confirmation of hours (historic values also include page visits). Used by
   * `OWNER_TUTORIAL` to auto-complete the "Configura horario" step.
   * Written by `confirmSetupStep()` on explicit owner acknowledgement; `serverTimestamp()` with first-write-wins.
   */
  hoursReviewedAt: timestampToDateNullable.nullable().optional(),
  /**
   * First time the owner opened `/catalogo` (products & services). Used by
   * `OWNER_TUTORIAL` to auto-complete the "Configura productos y servicios"
   * step. Written by `markCatalogReviewed()` on first catalog-page mount by
   * the owner; `serverTimestamp()` with first-write-wins.
   */
  catalogReviewedAt: timestampToDateNullable.nullable().optional(),
  /**
   * First-occurrence timestamps for the 6 activation milestones tracked by
   * the admin observability surface (spec/clubmanager/16 BR-3 + BR-13). Each entry is
   * `{ at: Timestamp, by: uid }` and is written with **set-if-missing**
   * semantics by the inline mutation hooks (booking · sale · staff invite)
   * + the `backfill_activation_for_club` admin callable for the other 3
   * (which reuse pre-existing fields: `firstCourtAt` from courts MIN,
   * `firstPricingRuleAt` from `pricesReviewedAt`, `firstAiNarrativeAt`
   * from `analyticsViewedAt`).
   *
   * Source-of-truth for the activation funnel chart on /admin/salud +
   * the "Activación" checklist in the admin club drawer. Never read by
   * customer-facing screens — purely admin/analytics.
   */
  activation: z
    .object({
      firstCourtAt: ActivationEventSchema.nullable().optional(),
      firstPricingRuleAt: ActivationEventSchema.nullable().optional(),
      firstBookingAt: ActivationEventSchema.nullable().optional(),
      firstSaleAt: ActivationEventSchema.nullable().optional(),
      firstStaffInviteAt: ActivationEventSchema.nullable().optional(),
      firstAiNarrativeAt: ActivationEventSchema.nullable().optional(),
    })
    .nullable()
    .optional(),
  /**
   * Denormalised health snapshot — spec/16 BR-15. Written nightly by the
   * `aggregate_platform_stats_daily` CF so the admin clubes table can
   * sort + display churn-risk without an N+1 fan-out across 5
   * sub-collections per row.
   *
   * Fields:
   *  - `churnRisk` — 0..100 integer, higher = riskier
   *  - `daysSinceLastBooking` / etc. — the 5 inputs to the score, kept
   *    so admins can hover-explain the number without re-querying.
   *  - `computedAt` — when the nightly CF wrote this. Stale snapshots
   *    (> 48h old) surface as "—" in the UI to avoid misleading triage.
   *
   * Never read by customer-facing screens. Owner / receptionist roles
   * can read their own club's healthSnapshot but the field exists
   * primarily for the platform-admin clubes table.
   */
  healthSnapshot: z
    .object({
      churnRisk: z.number().int().min(0).max(100).nullable().optional(),
      daysSinceLastBooking: z.number().int().nonnegative().nullable().optional(),
      weeklyActiveStaff: z.number().int().nonnegative().nullable().optional(),
      failedChargesLast30d: z.number().int().nonnegative().nullable().optional(),
      openNegativeFeedback: z.number().int().nonnegative().nullable().optional(),
      daysSinceFirstPaidCharge: z.number().int().nonnegative().nullable().optional(),
      computedAt: timestampToDateNullable.nullable().optional(),
    })
    .nullable()
    .optional(),
  createdAt: serverStampDate,
  updatedAt: serverStampDate,
  /** When any signed-in user from this club last opened the app. Updated
   * client-side from the auth-store when the current club is set; debounced
   * to ~1 write per 15 min per club so we don't hammer Firestore. */
  lastActivityAt: timestampToDateNullable.nullable().optional(),
});

export type Club = z.infer<typeof ClubSchema>;

// ─── Derived helpers ────────────────────────────────────────────────────────

/**
 * True for admin-built mockup clubs that haven't yet been claimed by an
 * owner. Use this for filtering / chip rendering in the admin picker.
 */
export function isClubMockup(c: Club): boolean {
  return c.status === "mockup";
}

/** Access level (full / read_only / blocked) for a club — the single arbiter
 * every gate, banner, and route guard reads. Derived from lifecycle `status`
 * + `billingStatus` + the deletion flag (see `clubAccessLevel`). A pending
 * deletion or `closed` lifecycle blocks; `past_due` billing is read-only;
 * `exempt`/`current`/`not_billable` never block. */
export function clubAccess(c: Club): ClubAccessLevel {
  return clubAccessLevel(c.status, c.billingStatus, c.deletionRequestedAt);
}

/** True when the club is still in the active trial window.
 *
 * Exempt clubs return false — they're not "in trial", they're permanently
 * exempt. This suppresses the trial-end countdown banner.
 */
export function isClubTrialActive(c: Club, now: Date = new Date()): boolean {
  if (c.billingStatus === "exempt") return false;
  return c.trialEndDate != null && c.status === "trial" && now < c.trialEndDate;
}

/** Days remaining in the trial (0 if expired or no trialEndDate). */
export function clubTrialDaysRemaining(
  c: Club,
  now: Date = new Date(),
): number {
  if (c.trialEndDate == null) return 0;
  const days = Math.floor(
    (c.trialEndDate.getTime() - now.getTime()) / 86_400_000,
  );
  if (days < 0) return 0;
  if (days > 999) return 999;
  return days;
}

/** True when the trial period has ended without converting to active.
 *
 * Exempt clubs return false — exemption supersedes any trial-expired state.
 */
export function isClubTrialExpired(c: Club, now: Date = new Date()): boolean {
  if (c.billingStatus === "exempt") return false;
  return c.trialEndDate != null && c.status === "trial" && now > c.trialEndDate;
}

/**
 * True when the Coach / Entrenador role is LIVE for this club. Self-serve GA
 * (v1.26.0): the sole gate is the owner opt-in `features.coachRoleEnabled` —
 * any club can turn the role on from Ajustes → Módulos → Entrenadores
 * without a platform/admin step. (The `platformFeatures.coachRole` capability flag no
 * longer gates anything; it's retained only as the Beta-badge key.) Every
 * coach surface (the "Entrenador" staff-invite option, the coach agenda lens,
 * the per-coach report) mounts on this. Mirrors the wallet's owner-opt-in
 * gate. Tolerates a null/undefined club so route guards can call it before the
 * club loads.
 */
export function coachRoleLive(c: Club | null | undefined): boolean {
  return Boolean(c?.features?.coachRoleEnabled);
}

/**
 * True when Torneos (tournaments/leagues) is LIVE for this club. Self-serve GA:
 * the sole gate is the owner opt-in `features.tournamentsEnabled` — any club
 * turns it on from Ajustes → Módulos → Torneos, no platform/admin step. The
 * Torneos section, its nav entry, and the create/run surfaces mount on this.
 * Tolerates a null/undefined club so route guards can call it before load.
 */
export function tournamentsLive(c: Club | null | undefined): boolean {
  return Boolean(c?.features?.tournamentsEnabled);
}

// ── Club Módulos — live resolvers (docs/PLAN-clubmanager-pwa.md M1) ─────────
// One pure resolver per activatable capability, mirroring `coachRoleLive` /
// `tournamentsLive` above, so `core/modules/registry.ts` reads a uniform
// `isLive(club)` shape regardless of which flag backs a module. All tolerate
// a null/undefined club. This increment adds the resolvers + the registry
// only — no route/nav is gated on `posLive` / `seriesLive` yet (tracked as
// the next increment); `onlineBookingsLive` / `crmLive` wrap existing fields
// that already gate their own surfaces (`/book/{slug}`, the Clientes tab).

/** True when the Monedero (player-credit wallet) is LIVE for this club. */
export function walletLive(c: Club | null | undefined): boolean {
  return Boolean(c?.features?.walletEnabled);
}

/**
 * True when the POS / Ventas módulo (product catalogue + quick-sale grid) is
 * LIVE for this club. Defaults `true` (grandfathered) via `features.posEnabled`.
 */
export function posLive(c: Club | null | undefined): boolean {
  return Boolean(c?.features?.posEnabled);
}

/**
 * True when the Series (recurring bookings) módulo is LIVE for this club.
 * Defaults `true` (grandfathered) via `features.seriesEnabled`.
 */
export function seriesLive(c: Club | null | undefined): boolean {
  return Boolean(c?.features?.seriesEnabled);
}

/** True when the public online-booking page (`/book/{slug}`) is LIVE. */
export function onlineBookingsLive(c: Club | null | undefined): boolean {
  return Boolean(c?.features?.onlineBookings);
}

/**
 * Why this club may NOT switch «Tu club en la app» on — `null` when it may.
 *
 * Turning the módulo on is the club asking to appear in the PLAYER DIRECTORY,
 * so the answer has to match the rule the projection already enforces:
 * `functions/places_projection.py :: club_is_projectable` requires
 * `not isDemo` and `status ∈ (trial, active)`.
 *
 * Before this, `modules-panel.tsx` gated the switch on `isOwner` and nothing
 * else. A demo or maqueta owner could flip it, watch the card turn green, and
 * be told their club was listed — while the projection silently skipped it,
 * forever. A switch that reports success and changes nothing is worse than one
 * that refuses: the refusal can be explained. Measured 2026-08-12: two of three
 * dev clubs sat in exactly that state (`isDemo: true`, `onlineBookings: true`).
 *
 * Derived from the projection's conditions rather than restating them, so the
 * two cannot drift apart. If `_PROJECTABLE_STATUSES` changes, change both.
 */
export type OnlineBookingsBlockReason = "demo" | "mockup" | "inactive";

export function onlineBookingsBlockedBecause(
  c: Club | null | undefined,
): OnlineBookingsBlockReason | null {
  if (!c) return null;
  if (c.isDemo) return "demo";
  if (c.status === "mockup") return "mockup";
  // Mirrors `_PROJECTABLE_STATUSES = ("trial", "active")`.
  if (c.status !== "trial" && c.status !== "active") return "inactive";
  return null;
}

/** True when this club is allowed to turn «Tu club en la app» on at all. */
export function canActivateOnlineBookings(c: Club | null | undefined): boolean {
  return onlineBookingsBlockedBecause(c) === null;
}

/** True when the CRM / Clientes tab is LIVE for this club. */
export function crmLive(c: Club | null | undefined): boolean {
  return Boolean(c?.features?.crmEnabled);
}
